import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarCheckIcon, ChevronDownIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { company } from '../data/company';
import { services } from '../data/services';
import { FlagES, FlagUS } from './ui/Flags';
import type { TranslationKey } from '../data/translations';
import type { Lang } from '../types';
const EASE = [0.23, 1, 0.32, 1] as const;
const navLinks: {
  key: TranslationKey;
  to: string;
}[] = [{
  key: 'nav.services',
  to: '/#services'
}, {
  key: 'nav.projects',
  to: '/#projects'
}, {
  key: 'nav.areas',
  to: '/#service-areas'
}, {
  key: 'nav.about',
  to: '/#about'
}, {
  key: 'nav.blog',
  to: '/#blog'
}, {
  key: 'nav.contact',
  to: '/#contact'
}];
function LanguageToggle({
  size = 'md'
}: {
  size?: 'md' | 'sm';
}) {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const options: {
    value: Lang;
    Flag: typeof FlagUS;
    label: string;
    aria: TranslationKey;
  }[] = [{
    value: 'en',
    Flag: FlagUS,
    label: 'EN',
    aria: 'nav.langEn'
  }, {
    value: 'es',
    Flag: FlagES,
    label: 'ES',
    aria: 'nav.langEs'
  }];
  return <div className="flex items-center gap-1.5" role="group" aria-label={t('nav.language')} data-id="element-88" data-mp="jkkp4ziilwi5">
      {options.map(({
      value,
      Flag,
      label,
      aria
    }) => {
      const active = lang === value;
      return <button key={value} type="button" onClick={() => setLang(value)} aria-pressed={active} aria-label={t(aria)} className={['group inline-flex items-center gap-1.5 rounded border bg-white transition-[border-color,box-shadow,transform] duration-300 ease-smooth hover:-translate-y-0.5 hover:shadow-sm', size === 'sm' ? 'h-9 px-2' : 'h-10 px-2.5', active ? 'border-cta-light shadow-sm' : 'border-navy/15 hover:border-cta-light/60'].join(' ')} data-id="element-89" data-mp="3n1j9zbts5oxo">
            <Flag className={['rounded-[2px] ring-1 ring-black/10 transition-transform duration-300 ease-smooth group-hover:scale-105', size === 'sm' ? 'h-[14px] w-5' : 'h-[17px] w-6'].join(' ')} data-id="element-90" data-mp="3196t51blxmih" />
            <span className={['text-[0.7rem] font-bold tracking-wide', active ? 'text-navy' : 'text-ink-muted'].join(' ')} data-id="element-91" data-mp="362i3vdjjinfo">
              {label}
            </span>
          </button>;
    })}
    </div>;
}
function ServicesDropdown({
  isActive
}: {
  isActive: boolean;
}) {
  const {
    t,
    b
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  return <div ref={wrapperRef} className="relative" onMouseEnter={() => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  }} onMouseLeave={() => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }} data-id="element-92" data-mp="sw1y0snfkybi">
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-haspopup="true" className={['group relative inline-flex h-[70px] items-center gap-1 px-[15px] text-sm font-semibold transition-colors duration-300 ease-smooth', isActive ? 'text-cta-dark' : 'text-navy hover:text-cta-dark'].join(' ')} data-id="element-93" data-mp="o30hkg7ksqpt">
        {t('nav.services')}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ease-smooth ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-94" data-mp="3f3rcusk7zu7f" />
        <span className={['absolute bottom-[22px] left-[15px] h-0.5 origin-left bg-cta-light transition-transform duration-300 ease-out', isActive || open ? 'w-[calc(100%-30px)] scale-x-100' : 'w-[calc(100%-30px)] scale-x-0 group-hover:scale-x-100'].join(' ')} aria-hidden="true" data-id="element-95" data-mp="3b3z0e4rph3ws" />
      </button>

      <AnimatePresence data-id="element-96" data-mp="3u1tgt3v5yhp7">
        {open && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="absolute left-1/2 top-full z-50 w-[26rem] -translate-x-1/2 pt-1" data-id="element-97" data-mp="3tyxkfjyys12v">
            <div className="overflow-hidden rounded-lg border border-navy/10 bg-white shadow-xl" data-id="element-98" data-mp="28bmp0a0e80xs">
              <ul className="grid grid-cols-2 gap-0.5 p-2" data-id="element-99" data-mp="2dar39f26couz">
                {services.map(service => {
              const Icon = service.icon;
              return <li key={service.slug} data-id="element-100" data-mp="2mamgrqj7cvy6">
                      <Link to={`/services/${service.slug}`} onClick={() => setOpen(false)} className="flex h-full items-start gap-2.5 rounded-md px-3 py-2.5 text-sm font-semibold leading-snug text-ink-soft transition-colors duration-200 ease-smooth hover:bg-cta-light/15 hover:text-navy" data-id="element-101" data-mp="2r8nwq2lc0f5f">
                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" aria-hidden="true" data-id="element-102" data-mp="39yxckfa1k5fl" />
                        {b(service.title)}
                      </Link>
                    </li>;
            })}
              </ul>
              <Link to="/#services" onClick={() => setOpen(false)} className="block border-t border-navy/10 bg-mist px-4 py-3 text-center text-sm font-bold text-brand-700 transition-colors duration-200 ease-smooth hover:text-cta-dark" data-id="element-103" data-mp="1ewitgv6vsshp">
                {t('nav.servicesMenu')}
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Navbar() {
  const {
    t,
    b
  } = useLanguage();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const current = `${location.pathname}${location.hash}`;
  const isServicesActive = location.pathname.startsWith('/services') || current === '/#services';
  useEffect(() => {
    setOpen(false);
  }, [location]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  return <>
      <motion.header initial={reduceMotion ? false : {
      y: -24,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.45,
      ease: EASE
    }} className={['fixed inset-x-0 top-0 z-[1000] border-b transition-[background-color,box-shadow,border-color] duration-300 ease-smooth', scrolled ? 'border-navy/10 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)]' : 'border-transparent bg-white/95 backdrop-blur-md'].join(' ')} data-id="element-104" data-mp="2f2mi96xcl0dd">
        <div className="mx-auto flex h-[70px] max-w-[1400px] items-center gap-5 px-4 md:px-8 lg:px-10" data-id="element-105" data-mp="3lkst5z1af41y">
          <motion.div initial={reduceMotion ? false : {
          x: -16,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} transition={{
          duration: 0.45,
          ease: EASE,
          delay: 0.05
        }} data-id="element-106" data-mp="3qh0302wt0zp6">
            <Link to="/" className="flex shrink-0 items-center gap-3 transition-opacity duration-300 ease-smooth hover:opacity-80" aria-label={company.name} data-id="element-107" data-mp="29d0hiezmj6ky">
              <img src={company.logo} alt="" className="h-10 w-10 shrink-0 rounded object-contain" width={40} height={40} data-id="element-108" data-mp="1uli48oy5fq5a" />
              <span className="hidden sm:block" data-id="element-109" data-mp="1lka5y6vmupb7">
                <span className="block font-display text-sm font-bold leading-tight text-[#1A3A52]" data-id="element-110" data-mp="1ln64nxfb1i0p">
                  IG Contracting &amp; Foundation Systems
                </span>
                <span className="mt-0.5 block text-[11px] italic leading-tight text-ink-muted" data-id="element-111" data-mp="3fykvyinyrpik">
                  {b(company.tagline)}
                </span>
              </span>
            </Link>
          </motion.div>

          <nav className="mx-auto hidden h-[70px] items-center xl:flex" aria-label="Main" data-id="element-112" data-mp="3ihg5vi8xpml7">
            {navLinks.map((link, index) => {
            const active = current === link.to;
            if (link.key === 'nav.services') {
              return <motion.div key={link.to} initial={reduceMotion ? false : {
                y: -8,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                duration: 0.3,
                ease: EASE,
                delay: 0.1 + index * 0.06
              }} data-id="element-113" data-mp="11pwepl6kq3a">
                    <ServicesDropdown isActive={isServicesActive} data-id="element-114" data-mp="39i1jatvw1w5z" />
                  </motion.div>;
            }
            return <motion.div key={link.to} initial={reduceMotion ? false : {
              y: -8,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              duration: 0.3,
              ease: EASE,
              delay: 0.1 + index * 0.06
            }} data-id="element-115" data-mp="oslshywo67gc" data-mpi={index}>
                  <Link to={link.to} aria-current={active ? 'page' : undefined} className={['group relative inline-flex h-[70px] items-center px-[15px] text-sm font-semibold transition-colors duration-300 ease-smooth', active ? 'text-cta-dark' : 'text-navy hover:text-cta-dark'].join(' ')} data-id="element-116" data-mp="cvc0ccakv0vp">
                    {t(link.key)}
                    <span className={['absolute bottom-[22px] left-[15px] h-0.5 w-[calc(100%-30px)] origin-left bg-cta-light transition-transform duration-300 ease-out', active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'].join(' ')} aria-hidden="true" data-id="element-117" data-mp="3u5jxoy537uxz" />
                  </Link>
                </motion.div>;
          })}
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-4 xl:ml-0" data-id="element-118" data-mp="33uq4araikw74">
            <div className="hidden sm:block" data-id="element-119" data-mp="2yx7z9bzmb14e">
              <LanguageToggle data-id="element-120" data-mp="2yyj1v8hbu2tx" />
            </div>

            <a href={company.phoneHref} aria-label={t('nav.phoneAria')} className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-cta text-white shadow-sm transition-[background-color,box-shadow,transform] duration-300 ease-smooth hover:-translate-y-0.5 hover:bg-cta-dark hover:shadow-md md:w-auto md:gap-2 md:px-4" data-id="element-121" data-mp="2u4etcksexmqq">
              <PhoneIcon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" data-id="element-122" data-mp="2b5ssn70t5xnc" />
              <span className="hidden text-sm font-bold leading-none md:inline" data-id="element-123" data-mp="2680x830yftee">{company.phone}</span>
            </a>

            <motion.div animate={reduceMotion ? undefined : {
            scale: [1, 1.035, 1]
          }} transition={{
            duration: 1.1,
            ease: 'easeInOut',
            repeat: Infinity,
            repeatDelay: 2.4
          }} className="hidden md:block" data-id="element-124" data-mp="9e65xhny8owo">
              <Link to="/#contact" className="inline-flex h-10 items-center gap-2 rounded-md bg-cta px-6 text-sm font-bold leading-none text-white shadow-sm transition-[background-color,box-shadow,transform] duration-300 ease-smooth hover:-translate-y-0.5 hover:bg-cta-dark hover:shadow-[0_6px_16px_rgba(226,113,29,0.45)]" data-id="element-125" data-mp="23n2ygmqki98m">
                <CalendarCheckIcon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" data-id="element-126" data-mp="2hnngvhskm31l" />
                {t('cta.inspection')}
              </Link>
            </motion.div>

            <button type="button" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="mobile-menu" aria-label={t('nav.menu')} className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-navy/15 text-navy transition-colors duration-300 ease-smooth hover:border-cta-light hover:text-cta-dark xl:hidden" data-id="element-127" data-mp="28le7iv91gfps">
              <MenuIcon className="h-6 w-6" aria-hidden="true" data-id="element-128" data-mp="tqwi8f8movgq" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Spacer matching the fixed header height */}
      <div className="h-[70px]" aria-hidden="true" data-id="element-129" data-mp="13l6jkmfjt43l" />

      <AnimatePresence data-id="element-130" data-mp="ynwjmcuj9a1i">
        {open && <motion.div id="mobile-menu" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="fixed inset-0 z-[1100] flex flex-col bg-[#1A3A52]/95 backdrop-blur-sm xl:hidden" role="dialog" aria-modal="true" aria-label={t('nav.menu')} data-id="element-131" data-mp="ym3c6vc3ilsg">
            <div className="flex items-center justify-between px-4 py-4 md:px-8" data-id="element-132" data-mp="3od8dn4umkm5n">
              <span className="font-display text-base font-bold text-white" data-id="element-133" data-mp="3ob5f1wfepkpb">IG Foundation Systems</span>
              <button type="button" onClick={() => setOpen(false)} aria-label={t('nav.closeMenu')} className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/25 text-white transition-colors duration-300 ease-smooth hover:bg-white hover:text-navy" data-id="element-134" data-mp="2mn0ek5hp2apa">
                <XIcon className="h-7 w-7" aria-hidden="true" data-id="element-135" data-mp="30fzqjxi9pms3" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 pb-6 md:px-8" aria-label="Mobile" data-id="element-136" data-mp="35d022nxvzhql">
              <ul className="divide-y divide-white/10" data-id="element-137" data-mp="3ab1g2ugggqv7">
                {navLinks.map(link => <li key={link.to} data-id="element-138" data-mp="3eepnnd537mgg">
                    <Link to={link.to} onClick={() => setOpen(false)} className="flex h-[60px] items-center rounded-md px-3 text-lg font-semibold text-white transition-colors duration-300 ease-smooth hover:bg-white/10 hover:text-cta-light" data-id="element-139" data-mp="3jdapfgzzme70">
                      {t(link.key)}
                    </Link>
                  </li>)}
              </ul>

              <div className="mt-8 flex items-center justify-between gap-4" data-id="element-140" data-mp="1co8rsvoihstb">
                <LanguageToggle size="sm" data-id="element-141" data-mp="17p5xa565r4zl" />
                <a href={company.phoneHref} aria-label={t('nav.phoneAria')} className="inline-flex items-center gap-2 text-base font-bold text-white" data-id="element-142" data-mp="17q7b2hek65l0">
                  <PhoneIcon className="h-5 w-5 text-cta-light" aria-hidden="true" data-id="element-143" data-mp="1vfic7zztm1zo" />
                  {company.phone}
                </a>
              </div>
            </nav>

            <div className="border-t border-white/10 p-4 md:p-8" data-id="element-144" data-mp="1qem0x1d53dk8">
              <Link to="/#contact" onClick={() => setOpen(false)} className="flex w-full items-center justify-center gap-2 rounded-lg bg-cta px-6 py-4 text-base font-bold text-white transition-colors duration-300 ease-smooth hover:bg-cta-dark" data-id="element-145" data-mp="1mdsc85rvhuqa">
                <CalendarCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-146" data-mp="ci5a13wgql75" />
                {t('cta.scheduleInspection')}
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}